import type {
  CanManageBillingFn,
  FetchBillingMethodsFn,
  NewPaymentMethodFn,
  SelectPaymentMethodFn,
} from '@/features/billing'
import { type BillingMethod } from '@/features/billing/profile/BillingMethod'
import { type Member } from '@/features/iam/members/Member'
import { type CouponSelectionViewModelFactory } from './CouponSelectionViewModel'
import { type QueueChange, TeamSelection } from './DraftPersister'
import { Job } from '@/features/jobs/Job'
import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import { combineLatest, EMPTY, type Observable } from 'rxjs'
import { catchError, map, switchMap } from 'rxjs/operators'
import { assertNotNil } from '@/utils/error-util'
import { fromStream, tapAction, toStream } from '@/utils/mobx-util'
import {
  filterNonNil,
  shareLatest,
  whileActive,
  withAbortSignal,
} from '@/utils/rx-util'
import { type ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type {
  AlertErrorHandlerFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'

/**
 * Draft slice for the payment method and coupon of the job.
 */
export class BillingViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * The selected billing method, if any.
   */
  @observable private accessor _selectedPaymentMethod: BillingMethod | null =
    null

  /**
   * Whether to use external billing or not.
   */
  @observable private accessor _bypassBilling = false

  /**
   * Coupon selection.
   */
  readonly couponSelection

  /**
   * Whether billing is relevant.
   */
  readonly isBillingRelevant$

  /**
   * The context used to look up the
   * relevant methods.
   *
   * @private
   */
  private readonly _context$

  /**
   * Streams as Observables.
   *
   * @private
   */
  private readonly _$

  /**
   * Whether the current user may manage billing for the context.
   *
   * @private
   */
  @observable private accessor _canManageContext = false

  constructor(
    private readonly _member$: Observable<Member | null>,
    private readonly _team$: Observable<TeamSelection | null>,
    private readonly _job$: Observable<Job>,
    private readonly _canFetchBillingInfo$: Observable<boolean>,
    private readonly _queueChange: QueueChange,
    private readonly _fetchBillingMethods: FetchBillingMethodsFn,
    private readonly _canManageBilling: CanManageBillingFn,
    private readonly _selectPaymentMethod: SelectPaymentMethodFn,
    private readonly _newPaymentMethod: NewPaymentMethodFn,
    private readonly _createCouponSelectionViewModel: CouponSelectionViewModelFactory,
    private readonly _isDisabled: () => boolean,
    private readonly _canEditDraftJobsAsAdmin: () => boolean,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
    private readonly _isAllowedToSubmit: () => boolean,
  ) {
    this.couponSelection = this._createCouponSelectionViewModel(
      this._member$,
      this._job$,
      this._queueChange,
      () => this.disabled,
    )

    this.isBillingRelevant$ = toStream(() => this.isBillingRelevant, true).pipe(
      shareLatest(),
      whileActive(this._lifecycle.active$),
    )

    this._context$ = combineLatest([
      this._member$.pipe(filterNonNil()),
      this._team$.pipe(filterNonNil()),
      this._job$,
    ]).pipe(
      map(([member, team, job]): BillingContext => ({ member, team, job })),
      shareLatest(),
    )

    this._$ = {
      job: fromStream(Job.none, this._job$),
      context: fromStream(null, this._context$),
    }

    this._lifecycle.manage(this.couponSelection)
    this._lifecycle.manage(
      this._context$.pipe(
        switchMap((ctx) =>
          this._canManageBilling(billingOwner(ctx.member, ctx.team)),
        ),
        tapAction((canManageContext) => {
          this._canManageContext = canManageContext
        }),
      ),
    )

    // Fetches the billing info when the relevant stuff becomes
    // available/changes.
    this._lifecycle.manage(
      this._context$.pipe(
        switchMap((ctx) =>
          combineLatest([
            this.isBillingRelevant$,
            this._canFetchBillingInfo$,
          ]).pipe(
            switchMap(([isBillingRelevant, canFetchBillingInfo]) =>
              isBillingRelevant && canFetchBillingInfo
                ? withAbortSignal((signal) =>
                    this._createAlertRetryWrapper(
                      'Loading payment methods failed',
                      signal,
                    )(() => this.fetchBillingInfo(ctx, signal)),
                  ).pipe(
                    // No payment method is preselected then; the next
                    // context change fetches again.
                    catchError(() => EMPTY),
                  )
                : EMPTY,
            ),
          ),
        ),
      ),
    )
  }

  /**
   * Whether to use external billing or not.
   */
  @computed
  get bypassBilling() {
    return this._bypassBilling
  }

  /**
   * The selected payment method if relevant.
   */
  @computed
  get selectedPaymentMethod() {
    return this.shouldCollectPaymentInfo ? this._selectedPaymentMethod : null
  }

  /**
   * Whether the controls for this editor are disabled.
   */
  @computed
  get disabled() {
    return this._isDisabled() || this.isLoading
  }

  /**
   * Whether the billing info is loading.
   */
  @computed
  get isLoading() {
    return this.fetchBillingInfo.pending
  }

  /**
   * Whether we are allowed to change the method.
   */
  @computed
  get canChange() {
    const ctx = this._$.context()
    if (!ctx) {
      return false
    }

    return this._canManageContext
  }

  /**
   * Whether current member can bypass billing.
   */
  @computed
  get canBypassBilling() {
    if (!this._canEditDraftJobsAsAdmin()) {
      return false
    }

    if (!this.isBillingRelevant) {
      return false
    }

    const ctx = this._$.context()

    return ctx ? ctx.job.owner !== ctx.member : false
  }

  /**
   * Whether billing is relevant.
   */
  @computed
  get isBillingRelevant() {
    const job = this._$.job()
    return job.isBillingEnabled && this._isAllowedToSubmit()
  }

  /**
   * Whether we should collect payment info.
   */
  @computed
  get shouldCollectPaymentInfo() {
    return this.isBillingRelevant && !this._$.job().deferPaymentInfoCollection
  }

  /**
   * Whether coupons can be used for the job.
   */
  @computed
  get canUseCoupons() {
    return this.isBillingRelevant && this.couponSelection.canUseCoupons
  }

  /**
   * Whether to show the applied coupon.
   */
  @computed
  get isAppliedCouponShown() {
    return (
      !this.canBypassBilling &&
      this.isBillingRelevant &&
      this.couponSelection.isCouponSelected
    )
  }

  /**
   * Whether to show the section for picking a coupon.
   */
  @computed
  get isCouponPickerShown() {
    return (
      !this.canBypassBilling &&
      this.canUseCoupons &&
      !this.couponSelection.isCouponSelected
    )
  }

  /**
   * Whether the billing selection is valid.
   */
  @computed
  get isValid() {
    if (!this.shouldCollectPaymentInfo) {
      return true
    }

    return Boolean(this.selectedPaymentMethod)
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Fetches the applied coupon.
   *
   * @param couponId
   * @param signal
   */
  fetchCouponInfo(couponId: string | null, signal: AbortSignal) {
    return this.couponSelection.fetchCouponInfo(couponId, signal)
  }

  /**
   * Fetches billing information for the specified member/team.
   */
  fetchBillingInfo = task.resolved(
    async ({ member, team }: BillingContext, signal?: AbortSignal) => {
      const methods = await this._fetchBillingMethods(
        billingOwner(member, team),
      )
      signal?.throwIfAborted()
      // Since we don't currently save the billing method on the Job,
      // we need to select the billing method that is the current default.
      const defaultMethod = methods.find((m) => m.isDefault) ?? null
      this.setMethod(defaultMethod)
    },
  )

  /**
   * Selects a Billing Method.
   */
  @action.bound
  async selectMethod() {
    const ctx = this._$.context()
    if (!ctx) {
      return
    }

    // Shows a modal for selecting a method.
    await this._selectPaymentMethod(
      ctx.member,
      teamOrNull(ctx.team),
      this.selectedPaymentMethod,
      this.saveMethod,
      this._lifecycle.signal,
    ).catch(
      this._createAlertErrorHandler('Selecting the payment method failed'),
    )
  }

  /**
   * Starts the New Payment Method flow.
   */
  @action.bound
  async newPaymentMethod() {
    await this._addNewPaymentMethod().catch(
      this._createAlertErrorHandler('Adding the payment method failed'),
    )
  }

  /**
   * Sets the billing method.
   * @param method
   */
  @action.bound
  setMethod(method: BillingMethod | null) {
    this._selectedPaymentMethod = method
  }

  /**
   * Saves the billing method. A failure is alerted by the draft editor.
   *
   * @param method
   */
  @action.bound
  async saveMethod(method: BillingMethod) {
    const result = await this._queueChange({
      type: 'BillingMethodSelected',
      billingMethod: method,
    })
    if (!result.errorMessage) {
      this.setMethod(method)
    }
  }

  /**
   * Sets external billing option.
   * @param value
   */
  @action.bound
  setBypassBilling(value: boolean) {
    this._bypassBilling = value
  }

  /**
   * Runs the New Payment Method flow and saves the added method.
   */
  private async _addNewPaymentMethod() {
    const ctx = this._$.context()
    const member = assertNotNil(ctx?.member, 'No workspace selected.')
    const teamSelection = assertNotNil(
      ctx?.team,
      'No team or individual selected.',
    )
    const method = await this._newPaymentMethod(
      member,
      teamOrNull(teamSelection),
      this._lifecycle.signal,
    )

    if (!method) {
      return
    }

    await this.saveMethod(method)
  }
}

function billingOwner(member: Member, team: TeamSelection) {
  return team === TeamSelection.myself ? member : team
}

function teamOrNull(team: TeamSelection) {
  return team === TeamSelection.myself ? null : team
}

/**
 * Billing context.
 */
interface BillingContext {
  member: Member
  team: TeamSelection
  job: Job
}

/**
 * Factory for creating a {@link BillingViewModel}.
 */
export type BillingViewModelFactory = ReturnType<
  typeof createBillingViewModelFactory
>

/** @see BillingViewModelFactory */
export function createBillingViewModelFactory(
  fetchBillingMethods: FetchBillingMethodsFn,
  canManageBilling: CanManageBillingFn,
  selectPaymentMethod: SelectPaymentMethodFn,
  newPaymentMethod: NewPaymentMethodFn,
  createCouponSelectionViewModel: CouponSelectionViewModelFactory,
  createAlertErrorHandler: AlertErrorHandlerFactory,
  createAlertRetryWrapper: AlertRetryWrapperFactory,
) {
  return (
    member$: Observable<Member | null>,
    team$: Observable<TeamSelection | null>,
    job$: Observable<Job>,
    canFetchBillingInfo$: Observable<boolean>,
    queueChange: QueueChange,
    isDisabled: () => boolean,
    canEditDraftJobsAsAdmin: () => boolean,
    isAllowedToSubmit: () => boolean,
  ) =>
    new BillingViewModel(
      member$,
      team$,
      job$,
      canFetchBillingInfo$,
      queueChange,
      fetchBillingMethods,
      canManageBilling,
      selectPaymentMethod,
      newPaymentMethod,
      createCouponSelectionViewModel,
      isDisabled,
      canEditDraftJobsAsAdmin,
      createAlertErrorHandler,
      createAlertRetryWrapper,
      isAllowedToSubmit,
    )
}
